import { makeObservable, observable, computed, action } from "mobx";
import { getRegisteredDomain, isOriginAUrl } from "../../lib/utils/domains";
import { StatisticsNodeForRequestsForDomainNode, StatisticsNodeForRequestsForRegisteredDomain, StatisticsForRequestsForUrl, NetworkOriginStatisticsTree } from "../../State/EvidenceDB/DatabaseTypes";
import { OriginSortType, SortDirection } from "../../State/NetworkTrafficRecordingState";
import { getDomain } from '../../lib/utils/domains';

export const AllOption = "| all |";
export type AllOption = typeof AllOption;;
export const AllThirdPartiesOption = "| all third parties |";
export type AllThirdPartiesOption = typeof AllThirdPartiesOption;;

export class SingleOriginFilterState {
	_sortBy: OriginSortType = 'alphanumeric';
	_sortDirection: SortDirection = 'ascending';
	_domainOrUrlToFilterFor: string | AllOption | AllThirdPartiesOption = AllOption ;

	constructor(
		private getOriginNetworkStatisticsTree: (filterThirdParties: boolean) => NetworkOriginStatisticsTree,
		private getFirstPartyOrigin?: (() => string)
	) {
		this._domainOrUrlToFilterFor = getFirstPartyOrigin == null ? AllOption : AllThirdPartiesOption;
		makeObservable(this, {
			_sortBy: observable,
			_sortDirection: observable,
			_domainOrUrlToFilterFor: observable,
		});
	}


	@computed get originNetworkStatisticsTree() {
		return this.getOriginNetworkStatisticsTree(this.isFilterThirdParties)
	}

	@computed get supportsFilteringByThirdParties() {
		return this.getFirstPartyOrigin != null;
	}

/**
 * The filter to apply to the tab URL. Should be either a domain or URL.
 */
	@computed get domainOrUrlToFilterFor() {
		// validate that the value to filter for is still a valid value before returning it.
		const domainOrUrlToFilterFor = this._domainOrUrlToFilterFor;
		if (domainOrUrlToFilterFor === AllOption || domainOrUrlToFilterFor === AllThirdPartiesOption) {
			return domainOrUrlToFilterFor;
		}
		const domain = getDomain(domainOrUrlToFilterFor);
		if (domain == null) return AllOption;
		const registeredDomain = getRegisteredDomain(domain);
		if (registeredDomain == null) return AllOption;
		const rdNode = this.originNetworkStatisticsTree.get(registeredDomain);
		if (rdNode == null) return AllOption;
		if (registeredDomain === domainOrUrlToFilterFor) {
			// The filter is a registered domain, and we found it in the tree,
			// so it's valid and we can return it.
			return domainOrUrlToFilterFor;
		}
		// The filter is more than just a registered domain.
		const dNode = rdNode.domains.get(domain);
		if (dNode == null) {
			// The domain is not in the tree, and so the filter is out of date,
			// but since the registered domain is in the tree, we can filter on it
			// instead.
			return registeredDomain;
		}
		if (dNode.urls.get(domainOrUrlToFilterFor) != null) {
			// The filter is a valid URL, and we can return it straight-away.
			return domainOrUrlToFilterFor;
		} else {
			// The filter is either a domain, or a URL that is no longer
			// valid but contains a domain that is still valid.
			// So, we'll return the domain.
			return domain;
		}
	}

	@computed get isFilterAll() {
		return this._domainOrUrlToFilterFor == null ||
			this._domainOrUrlToFilterFor == AllOption
	}

	@computed get isFilterThirdParties() {
		return this._domainOrUrlToFilterFor == AllThirdPartiesOption
	}

	@computed get isFilterAllOrAllThirdParties() {
		return this.isFilterAll || this.isFilterThirdParties;
	}

	@computed get isFilterAUrl() {
		return !this.isFilterAllOrAllThirdParties &&
			 isOriginAUrl(this.domainOrUrlToFilterFor);
	}
		
	@computed get registeredDomainsObserved() {
		return [...this.originNetworkStatisticsTree.values()];
	}

	@computed get sortBy() { return this._sortBy; }
	@computed get sortDirection() { return this._sortDirection; }



	setSortBy = action((sortBy: OriginSortType) => { this._sortBy = sortBy; });
	setSortDirection = action((sortDirection: SortDirection) => { this._sortDirection = sortDirection; });
	setDomainOrUrlToFilterFor = action((domainOrUrlToFilterFor: string) => { this._domainOrUrlToFilterFor = domainOrUrlToFilterFor; });

	@computed private get sortAlpha() { return this.sortBy === 'alphanumeric'; }
	@computed get sortStringFn() {
		const sortDirectionNumeric = this.sortDirection === 'ascending' ? 1 : -1;
		return <T>(stringFn: (t: T) => string) => (a: T, b: T) => stringFn(a).localeCompare(stringFn(b)) * sortDirectionNumeric;
	}
	@computed get registeredDomainSortFn() {
		return this.sortStringFn<StatisticsNodeForRequestsForRegisteredDomain>(this.sortAlpha ? r => r.registeredDomain :
			r => this.sortDirection === "ascending" ? r.whenFirstObservedIso : r.whenLastObservedIso);
	}
	@computed get domainSortFn() {
		return this.sortStringFn<StatisticsNodeForRequestsForDomainNode>(this.sortAlpha ? r => r.domain :
			r => this.sortDirection === "ascending" ? r.whenFirstObservedIso : r.whenLastObservedIso);
	}
	@computed get UrlNodeSortFn() {
		return this.sortStringFn<StatisticsForRequestsForUrl>(this.sortAlpha ? r => r.url :
			r => this.sortDirection === "ascending" ? r.whenFirstObservedIso : r.whenLastObservedIso);
	}
 
	@computed get registeredDomainSelected() {
		return this.isFilterAllOrAllThirdParties ? this.domainOrUrlToFilterFor : getRegisteredDomain(this.domainOrUrlToFilterFor); }
	@computed get domainSelected() {
		return this.isFilterAllOrAllThirdParties ? AllOption : getDomain(this.domainOrUrlToFilterFor) ?? AllOption; }
	@computed get urlSelected() {
		return this.isFilterAllOrAllThirdParties || !this.isFilterAUrl ? AllOption : this.domainOrUrlToFilterFor ;
	}

	@computed get registeredDomains() {
		return this.registeredDomainsObserved.sort(this.registeredDomainSortFn).map(({ registeredDomain }) => registeredDomain);
	}
	@computed private get filteredDomainsObserved() {
		const firstPartyRegisteredDomain = getRegisteredDomain(this.getFirstPartyOrigin?.());
		const registeredDomainFilter: (_: {registeredDomain: string}) => boolean = this.isFilterAll ?
				() => true :
			this.isFilterThirdParties ?
				({ registeredDomain }) => registeredDomain !== firstPartyRegisteredDomain :
				({ registeredDomain }) => registeredDomain === this.registeredDomainSelected;
		return ([] as StatisticsNodeForRequestsForDomainNode[]).concat(...this.registeredDomainsObserved
			.filter(registeredDomainFilter)
			.map(({ domains }) => [...domains.values()])
		);
	}
	@computed get filteredDomains() { 
		return this.filteredDomainsObserved.sort(this.domainSortFn).map(({ domain }) => domain);
	}
	@computed private get filteredUrlsObserved() {
		return ([] as StatisticsForRequestsForUrl[]).concat(...this.filteredDomainsObserved
			.filter(({ domain }) => this.domainSelected == AllOption || domain.endsWith(this.domainSelected) )
			.map(({ urls }) => [...urls.values()])
		);
	}
	@computed get filteredUrls() {
		return this.filteredUrlsObserved.sort(this.UrlNodeSortFn).map(({ url }) => url);
	}

	urlFilter = (url: string) => {
		const thirdPartyOrigin = this.getFirstPartyOrigin?.();
		return this.isFilterAll ?
				true :
			this.isFilterThirdParties ?
				getRegisteredDomain(url) !== getRegisteredDomain(thirdPartyOrigin) :
			this.isFilterAUrl ?
				this.domainOrUrlToFilterFor === url :
				getDomain(url)?.endsWith(this.domainOrUrlToFilterFor) ?? false;
	}
}

export const urlFilterFor = (filterFor: string | AllOption | AllThirdPartiesOption) => {
	const thirdPartyRegisteredDomain = getRegisteredDomain(filterFor);
	return (url: string) => {
		return filterFor === AllOption ?
				true :
			filterFor === AllThirdPartiesOption ?
				getRegisteredDomain(url) !== thirdPartyRegisteredDomain :
			filterFor === url;
	}
}